// Typed REST client for the HTTP API. Admin calls attach the Cognito ID token.

import { loadConfig } from '../config';
import { currentIdToken } from '../auth/cognito';

export interface Topic {
  id: string;
  title: string;
  order: number;
  owner?: string;
  createdAt?: string;
}

export interface Question {
  id: string;
  topicId: string;
  order: number;
  text: string;
  points: number;
  answer?: string;
}

export interface Game {
  id: string;
  code: string;
  status: string;
  topicOrder: string[];
  createdBy?: string;
  createdAt: string;
  endedAt?: string;
}

export interface LeaderEntry {
  nickname: string;
  totalScore: number;
  games: number;
}

export interface ImportReport {
  imported: number;
  skipped: number;
  reports: { title: string; imported: boolean; questions: number; error?: string }[];
}

async function request<T>(method: string, path: string, body?: unknown, auth = false): Promise<T> {
  const cfg = await loadConfig();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    const token = await currentIdToken();
    if (!token) throw new Error('Not signed in');
    headers['authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(cfg.httpApiUrl + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Topics / questions (authoring — authenticated)
  listTopics: () => request<Topic[]>('GET', '/topics'),
  createTopic: (title: string, order = 0) => request<Topic>('POST', '/topics', { title, order }, true),
  updateTopic: (id: string, title: string, order: number) =>
    request<Topic>('PUT', `/topics/${id}`, { title, order }, true),
  deleteTopic: (id: string) => request<void>('DELETE', `/topics/${id}`, undefined, true),

  listQuestions: (topicId: string) => request<Question[]>('GET', `/topics/${topicId}/questions`, undefined, true),
  createQuestion: (topicId: string, q: { text: string; points: number; answer: string }) =>
    request<Question>('POST', `/topics/${topicId}/questions`, q, true),
  updateQuestion: (id: string, q: { text: string; points: number; answer: string; order?: number }) =>
    request<Question>('PUT', `/questions/${id}`, q, true),
  deleteQuestion: (id: string) => request<void>('DELETE', `/questions/${id}`, undefined, true),

  importContent: (payload: unknown) => request<ImportReport>('POST', '/import', payload, true),

  // Games
  createGame: (topicOrder: string[]) => request<Game>('POST', '/games', { topicOrder }, true),
  listGames: () => request<Game[]>('GET', '/games'),
  getGame: (id: string) =>
    request<{ game: Game; players: unknown[]; results: unknown[] }>('GET', `/games/${id}`),
  deleteGame: (id: string) => request<void>('DELETE', `/games/${id}`, undefined, true),

  leaderboard: () => request<LeaderEntry[]>('GET', '/leaderboard'),
};
